import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store/store';
import Layout from '../components/Layout';
import ProgressSteps from '../components/ProgressSteps';
import { updateClaimField, resetForm, setClaimId } from '../store/claimSlice';
import { fetchCitiesByState, searchFacilities, getStates, getFacilityStates, createClaim, updateClaim, buildDraftClaimPayload, getLossTypes, getClaimByToken } from '../services/api';
import { hydrateClaimIntoStore } from '../utils/claimTokenResume';
import { RootState } from '../store/store';
import { config } from '../config';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getErrorMessage } from '../utils/errorHandler';
import { FormControl, MenuItem, Select } from '@mui/material';
import banner from '../assets/tpp-claims-banner.jpg';
import packageJson from '../../package.json';
import '../styles/main.css';

const schema = yup.object().shape({
  facility_state: yup.string().required('Facility state is required'),
  facility_city: yup.string().required('Facility city is required'),
  facility_id: yup.string().required('Facility is required'),
  facility_unit: yup.string().required('Unit number is required'),
  reporter_first_name: yup.string().required('First name is required'),
  reporter_last_name: yup.string().required('Last name is required'),
  reporter_phone: yup.string()
    .required('Phone number is required')
    .test('phone-digits', 'Phone number must be 10 digits', (value) => {
      const digits = (value || '').replace(/\D/g, '');
      return digits.length === 10;
    }),
  owner_email: yup.string().email('Invalid email format').required('Email is required'),
  owner_address_1: yup.string().required('Address is required'),
  owner_address_2: yup.string().optional(),
  owner_city: yup.string().required('City is required'),
  owner_state: yup.string().required('State is required'),
  owner_zip_code: yup.string()
    .required('Zip code is required')
    .test('zip-format', 'Zip code must be 5 digits or 9 digits (12345-6789)', (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, '');
      return digits.length === 5 || digits.length === 9;
    }),
}) as yup.ObjectSchema<GetStartedFormData>;

const sanitizeZipInput = (val: string): string => {
  const digits = val.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return digits.slice(0, 5) + '-' + digits.slice(5);
};

const formatPhoneNumber = (digits: string): string => {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const FALLBACK_CREATE_KEYS = [
  'incident_date',
  'tpp_loss_type_id',
  'damaged_items',
  'damage_description',
];

const shouldFallbackToCreateDraft = (error: unknown): boolean => {
  const axiosError = error as { response?: { status?: number; data?: any } };
  if (axiosError?.response?.status !== 422) return false;
  const data = axiosError.response?.data;
  const backendErrors = data?.errors;
  if (backendErrors && typeof backendErrors === 'object') {
    const hasKnownFieldError = FALLBACK_CREATE_KEYS.some(
      (key) => Array.isArray(backendErrors[key]) && backendErrors[key].length > 0
    );
    if (hasKnownFieldError) return true;
  }

  const messageText = typeof data?.message === 'string' ? data.message.toLowerCase() : '';
  const flattenedErrorText = backendErrors && typeof backendErrors === 'object'
    ? Object.values(backendErrors)
        .flat()
        .filter((m): m is string => typeof m === 'string')
        .join(' ')
        .toLowerCase()
    : '';

  const combinedText = `${messageText} ${flattenedErrorText}`;
  return (
    combinedText.includes('incident date field is required') ||
    combinedText.includes('tpp loss type id field is required') ||
    combinedText.includes('damaged items field is required') ||
    combinedText.includes('damage description field is required')
  );
};

const todayYyyyMmDd = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface GetStartedFormData {
  facility_state: string;
  facility_city: string;
  facility_id: string;
  facility_unit: string;
  reporter_first_name: string;
  reporter_last_name: string;
  reporter_phone: string;
  owner_email: string;
  owner_address_1: string;
  owner_address_2?: string;
  owner_city: string;
  owner_state: string;
  owner_zip_code: string;
}

const GetStarted: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token?: string }>();
  const queryClaimToken = new URLSearchParams(location.search).get('claimToken') || '';
  const resumeToken = token || queryClaimToken;
  const dispatch = useDispatch<AppDispatch>();
  const { showError } = useSnackbar();
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [cityError, setCityError] = useState<string | null>(null);
  const [facilityError, setFacilityError] = useState<string | null>(null);
  const [states, setStates] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [facilities, setFacilities] = useState<Array<{ id: string; name: string }>>([]);
  const [facilityStateCodes, setFacilityStateCodes] = useState<string[]>([]);
  const [loadingFacilityStates, setLoadingFacilityStates] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in submit button disabled state and label
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cities = useSelector((state: RootState) => state.claim.cities);
  const claimData = useSelector((state: RootState) => state.claim);
  const isLoadingTestData = useRef(false);
  const testDataClickCount = useRef(0);
  /** Tracks last processed facility_state so we only clear city when the user changes state, not on remount (e.g. Back from Incident). */
  const prevFacilityStateRef = useRef<string | undefined>(undefined);
  const [isResumingByToken, setIsResumingByToken] = useState(false);

  useEffect(() => {
    if (!resumeToken) return;
    const resumeDraft = async () => {
      setIsResumingByToken(true);
      try {
        const existingClaim = await getClaimByToken(resumeToken);
        const status = String(existingClaim.status).toUpperCase();
        if (status === 'DRAFT') {
          hydrateClaimIntoStore(existingClaim, dispatch);
          return;
        }
        if (status === 'INCOMPLETE') {
          hydrateClaimIntoStore(existingClaim, dispatch);
          navigate(`/damages?claimToken=${encodeURIComponent(resumeToken)}`, { replace: true });
          return;
        }
        navigate(`/claim-status?claimToken=${encodeURIComponent(resumeToken)}`, { replace: true, state: { claim: existingClaim } });
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        showError(errorMessage || 'Unable to resume claim from this link.');
        navigate('/', { replace: true });
      } finally {
        setIsResumingByToken(false);
      }
    };
    resumeDraft();
  }, [resumeToken, dispatch, navigate, showError]);

  useEffect(() => {
    const fromIncidentBack = (location.state as { fromIncidentBack?: boolean })?.fromIncidentBack;
    const fromProgressSteps = (location.state as { fromProgressSteps?: boolean })?.fromProgressSteps;
    const fromTokenRoute = !!resumeToken;
    const hasDraftInStore =
      claimData.claimId != null ||
      claimData.facility_state !== '' ||
      claimData.facility_city !== '' ||
      claimData.facility_id !== '' ||
      claimData.facility_unit !== '' ||
      claimData.reporter_first_name !== '' ||
      claimData.owner_email !== '';
    if (!fromIncidentBack && !fromProgressSteps && !fromTokenRoute && !hasDraftInStore) {
      dispatch(resetForm());
    }
  }, [
    dispatch,
    location.state,
    claimData.claimId,
    claimData.facility_state,
    claimData.facility_city,
    claimData.facility_id,
    claimData.facility_unit,
    claimData.reporter_first_name,
    claimData.owner_email,
    resumeToken,
  ]);

  // Filter states to only include those that have facilities (memoized for stable effect deps)
  const availableFacilityStates = useMemo(
    () => states.filter((state) => facilityStateCodes.includes(state.code)),
    [states, facilityStateCodes]
  );
  
  const { register, handleSubmit, formState: { errors }, control, setValue, getValues, watch } = useForm<GetStartedFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      facility_state: claimData.facility_state,
      facility_city: claimData.facility_city,
      facility_id: claimData.facility_id,
      facility_unit: claimData.facility_unit,
      reporter_first_name: claimData.reporter_first_name,
      reporter_last_name: claimData.reporter_last_name,
      reporter_phone: claimData.reporter_phone,
      owner_email: claimData.owner_email,
      owner_address_1: claimData.owner_address_1,
      owner_address_2: claimData.owner_address_2,
      owner_city: claimData.owner_city,
      owner_state: claimData.owner_state,
      owner_zip_code: claimData.owner_zip_code,
    },
  });

  // Initialize form fields from Redux store when component mounts or when claim data changes
  useEffect(() => {
    setValue('facility_state', claimData.facility_state);
    setValue('facility_city', claimData.facility_city);
    setValue('facility_id', claimData.facility_id);
    setValue('facility_unit', claimData.facility_unit);
    setValue('reporter_first_name', claimData.reporter_first_name);
    setValue('reporter_last_name', claimData.reporter_last_name);
    setValue('reporter_phone', formatPhoneNumber((claimData.reporter_phone || '').replace(/\D/g, '')));
    setValue('owner_email', claimData.owner_email);
    setValue('owner_address_1', claimData.owner_address_1);
    setValue('owner_address_2', claimData.owner_address_2);
    setValue('owner_city', claimData.owner_city);
    setValue('owner_state', claimData.owner_state);
    setValue('owner_zip_code', sanitizeZipInput(claimData.owner_zip_code || ''));
  }, [claimData, setValue]);

  /** watch() keeps facility fields in sync with RHF for effects + MUI Select (useWatch could lag vs Controller). */
  const facilityStateWatched = watch('facility_state');
  const facilityCityWatched = watch('facility_city');

  const loadTestData = () => {
    const randomUnit = Math.floor(1000 + Math.random() * 9000).toString(); // Generates random 4-digit number
    testDataClickCount.current += 1;

    if (testDataClickCount.current === 1) {
      // First click: Set FL state (triggers cities API call via useEffect)
      const firstClickData = {
        reporter_first_name: 'Sally' + randomUnit,
        reporter_last_name: 'Williams' + randomUnit,
        reporter_phone: '(555) 123-' + randomUnit,
        owner_email: 'sally' + randomUnit + '@example.com',
        owner_address_1: '123 Main St',
        owner_address_2: 'Apt 4B',
        owner_city: 'Miami',
        owner_state: 'FL',
        owner_zip_code: '33101',
        facility_unit: 'A' + randomUnit,
        facility_state: 'FL',
      };

      // Clear dependent fields first
      setValue('facility_city', '', { shouldValidate: false, shouldDirty: false });
      setValue('facility_id', '', { shouldValidate: false, shouldDirty: false });
      dispatch(updateClaimField({ field: 'facility_city', value: '' }));
      dispatch(updateClaimField({ field: 'facility_id', value: '' }));

      // Set all fields including facility_state to 'FL'
      // This will trigger the cities API call via the useEffect watching facility_state
      Object.entries(firstClickData).forEach(([field, value]) => {
        setValue(field as keyof GetStartedFormData, value, { shouldValidate: true, shouldDirty: true });
        dispatch(updateClaimField({ field: field as keyof GetStartedFormData, value }));
      });
    } else if (testDataClickCount.current === 2) {
      // Second click: Set Bonita Springs city (triggers facilities search API call via useEffect)
      isLoadingTestData.current = true;
      setValue('facility_city', 'Bonita Springs', { shouldValidate: true, shouldDirty: true });
      setValue('facility_id', '', { shouldValidate: false, shouldDirty: false }); // Clear facility until loaded
      dispatch(updateClaimField({ field: 'facility_city', value: 'Bonita Springs' }));
      dispatch(updateClaimField({ field: 'facility_id', value: '' }));
    } else {
      // Third click or more: Reset and start over
      testDataClickCount.current = 0;
      loadTestData(); // Recursive call to start from first click
    }
  };

  useEffect(() => {
    const loadCities = async () => {
      // useWatch can lag one frame behind setValue (e.g. after remount / browser back on iOS).
      // getValues() updates synchronously with setValue, so use it + Redux to avoid clearing
      // facility_city when the select briefly reports empty before options exist.
      const effectiveState =
        getValues('facility_state') || claimData.facility_state;

      if (!effectiveState) {
        setValue('facility_city', '', { shouldValidate: false, shouldDirty: false });
        dispatch(updateClaimField({ field: 'facility_city', value: '' }));
        prevFacilityStateRef.current = undefined;
        return;
      }

      const prev = prevFacilityStateRef.current;
      const userChangedState = prev !== undefined && prev !== effectiveState;

      if (userChangedState) {
        setValue('facility_city', '', { shouldValidate: false, shouldDirty: false });
        dispatch(updateClaimField({ field: 'facility_city', value: '' }));
      }

      setLoadingCities(true);
      setCityError(null);

      try {
        const resultAction = await dispatch(fetchCitiesByState(effectiveState));
        if (fetchCitiesByState.rejected.match(resultAction)) {
          const errorMessage = getErrorMessage(resultAction.error);
          setCityError('Failed to load cities. Please try again.');
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setCityError('Failed to load cities. Please try again.');
        showError(errorMessage);
      } finally {
        setLoadingCities(false);
        prevFacilityStateRef.current = effectiveState;
      }
    };

    loadCities();
  }, [facilityStateWatched, claimData.facility_state, dispatch, showError, setValue, getValues]);

  const fetchFacilitiesList = useCallback(
    async (stateCode: string, cityName: string) => {
      if (!stateCode || !cityName.trim()) {
        setFacilities([]);
        return;
      }

      setLoadingFacilities(true);
      setFacilityError(null);
      try {
        const resultAction = await dispatch(
          searchFacilities({ state: stateCode, city: cityName })
        );

        if (searchFacilities.fulfilled.match(resultAction)) {
          const loadedFacilities = resultAction.payload || [];
          setFacilities(loadedFacilities);
        } else if (searchFacilities.rejected.match(resultAction)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Rejected error structure:', resultAction.error);
          }
          const errorMessage = getErrorMessage(resultAction.error);
          setFacilityError('Failed to load facilities. Please try again.');
          setFacilities([]);
          isLoadingTestData.current = false;
          showError(errorMessage);
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setFacilityError('Failed to load facilities. Please try again.');
        setFacilities([]);
        isLoadingTestData.current = false;
        showError(errorMessage);
      } finally {
        setLoadingFacilities(false);
      }
    },
    [dispatch, showError]
  );

  // Load facilities when state/city change (restore, programmatic setValue, etc.)
  useEffect(() => {
    const state = facilityStateWatched || claimData.facility_state;
    const city = facilityCityWatched || claimData.facility_city;
    void fetchFacilitiesList(state, city);
  }, [
    facilityStateWatched,
    facilityCityWatched,
    claimData.facility_state,
    claimData.facility_city,
    fetchFacilitiesList,
  ]);

  // Watch for facilities to be loaded when test data flag is set
  useEffect(() => {
    if (isLoadingTestData.current && facilities.length > 0 && !loadingFacilities) {
      setValue('facility_id', facilities[0].id, { shouldValidate: true, shouldDirty: true });
      isLoadingTestData.current = false;
      testDataClickCount.current = 0; // Reset click count after facilities are loaded
    }
  }, [facilities, loadingFacilities, setValue]);

  // After each async API completes, apply saved session values (Redux) to <select>s.
  // Native selects stay blank until matching <option> elements exist; this runs when lists are ready.
  useEffect(() => {
    const opts = { shouldValidate: false as const, shouldDirty: false as const };

    const ownerCode = claimData.owner_state;
    if (!loadingStates && states.length > 0 && ownerCode) {
      if (states.some((s) => s.code === ownerCode)) {
        const cur = getValues('owner_state');
        if (cur !== ownerCode) {
          setValue('owner_state', ownerCode, opts);
        }
      }
    }

    const facilityStateCode = claimData.facility_state;
    if (!loadingStates && !loadingFacilityStates && facilityStateCode) {
      if (availableFacilityStates.some((s) => s.code === facilityStateCode)) {
        const cur = getValues('facility_state');
        if (cur !== facilityStateCode) {
          setValue('facility_state', facilityStateCode, opts);
        }
      }
    }

    const facilityCity = claimData.facility_city;
    if (!loadingCities && facilityCity) {
      if (cities.includes(facilityCity)) {
        const cur = getValues('facility_city');
        if (cur !== facilityCity) {
          setValue('facility_city', facilityCity, opts);
        }
      }
    }

    const facilityId = claimData.facility_id;
    if (!loadingFacilities && facilities.length > 0 && facilityId) {
      if (facilities.some((f) => String(f.id) === String(facilityId))) {
        // When the facility list is ready, state/city options are already initialized — set them here too
        // so all three stay in sync (fixes ordering/timing where only facility_id matched before).
        const savedState = claimData.facility_state;
        if (savedState && availableFacilityStates.some((s) => s.code === savedState)) {
          if (getValues('facility_state') !== savedState) {
            setValue('facility_state', savedState, opts);
          }
        }
        const savedCity = claimData.facility_city;
        if (savedCity && cities.includes(savedCity)) {
          if (getValues('facility_city') !== savedCity) {
            setValue('facility_city', savedCity, opts);
          }
        }
        const cur = getValues('facility_id');
        if (String(cur) !== String(facilityId)) {
          setValue('facility_id', String(facilityId), opts);
        }
      }
    }
  }, [
    loadingStates,
    loadingFacilityStates,
    loadingCities,
    loadingFacilities,
    states,
    availableFacilityStates,
    cities,
    facilities,
    claimData.owner_state,
    claimData.facility_state,
    claimData.facility_city,
    claimData.facility_id,
    getValues,
    setValue,
  ]);

  // Load all states first (for owner_state dropdown and to get state names)
  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const statesData = await getStates();
        setStates(statesData);
      } catch (error) {
        console.error('Error loading states:', error);
        const errorMessage = getErrorMessage(error);
        showError(errorMessage);
      } finally {
        setLoadingStates(false);
      }
    };

    loadStates();
  }, [showError]);

  // Load facility state codes after states are loaded
  useEffect(() => {
    const loadFacilityStates = async () => {
      setLoadingFacilityStates(true);
      try {
        const facilityStatesData: any = await getFacilityStates();
        // API returns array of state codes (strings like ["AZ", "CO", "CT", ...])
        // Handle both array of strings or array of objects
        let stateCodes: string[] = [];
        if (Array.isArray(facilityStatesData)) {
          stateCodes = facilityStatesData.map((item: any) => {
            if (typeof item === 'string') {
              return item;
            } else if (item && typeof item === 'object') {
              return item.code || item.state || item.id || '';
            }
            return '';
          }).filter((code: string) => code !== '');
        }
        setFacilityStateCodes(stateCodes);
      } catch (error) {
        console.error('Error loading facility states:', error);
        const errorMessage = getErrorMessage(error);
        showError(errorMessage);
      } finally {
        setLoadingFacilityStates(false);
      }
    };

    loadFacilityStates();
  }, [showError]);

  const onSubmit = async (data: GetStartedFormData) => {
    Object.entries(data).forEach(([field, value]) => {
      if (value !== undefined) {
        dispatch(updateClaimField({ field: field as keyof GetStartedFormData, value }));
      }
    });
    setIsSubmitting(true);
    try {
      const payload = {
        facility_state: data.facility_state,
        facility_city: data.facility_city,
        facility_id: data.facility_id,
        facility_unit: data.facility_unit,
        reporter_first_name: data.reporter_first_name,
        reporter_last_name: data.reporter_last_name,
        reporter_phone: data.reporter_phone,
        owner_email: data.owner_email,
        owner_address_1: data.owner_address_1,
        owner_address_2: data.owner_address_2 || '',
        owner_city: data.owner_city,
        owner_state: data.owner_state,
        owner_zip_code: data.owner_zip_code,
      };

      const createDraftWithValidationFallback = async () => {
        try {
          return await createClaim(payload);
        } catch (createErr) {
          if (!shouldFallbackToCreateDraft(createErr)) {
            throw createErr;
          }

          let fallbackLossTypeId = claimData.tpp_loss_type_id || '';
          if (!fallbackLossTypeId) {
            try {
              const lossTypes = await getLossTypes();
              fallbackLossTypeId = lossTypes.length > 0 ? String(lossTypes[0].id) : '';
            } catch {
              fallbackLossTypeId = '1';
            }
          }

          const bootstrapPayload = {
            ...payload,
            incident_date: claimData.incident_date || todayYyyyMmDd(),
            tpp_loss_type_id: fallbackLossTypeId || '1',
            damaged_items: claimData.damaged_items || 'To be provided',
            damage_description: claimData.damage_description || 'To be provided',
          };

          const created = await createClaim(bootstrapPayload);
          dispatch(updateClaimField({ field: 'incident_date', value: bootstrapPayload.incident_date }));
          dispatch(updateClaimField({ field: 'tpp_loss_type_id', value: bootstrapPayload.tpp_loss_type_id }));
          dispatch(updateClaimField({ field: 'damaged_items', value: bootstrapPayload.damaged_items }));
          dispatch(updateClaimField({ field: 'damage_description', value: bootstrapPayload.damage_description }));
          return created;
        }
      };

      const existingId = claimData.claimId;
      if (existingId != null) {
        try {
          await updateClaim(existingId, buildDraftClaimPayload(payload));
        } catch (updateErr) {
          // If an old non-draft claim id is still in memory, backend may require later-step fields.
          // Create a fresh draft instead of blocking Step 1.
          if (!shouldFallbackToCreateDraft(updateErr)) {
            throw updateErr;
          }
          const newClaim = await createDraftWithValidationFallback();
          dispatch(setClaimId(newClaim.id));
        }
      } else {
        const newClaim = await createDraftWithValidationFallback();
        dispatch(setClaimId(newClaim.id));
      }
      navigate('/incident', { state: { showStep1CompleteOverlay: true } });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showError(errorMessage || 'Failed to save claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** MUI Select (non-native) matches Incident page — reliable on iOS vs. native HTML select. 16px text avoids zoom-on-focus. */
  const muiSelectFormSx = {
    width: '100%',
    '& .MuiSelect-select': {
      fontSize: 'max(16px, 1rem)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--border-color)',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-color)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'var(--primary-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--error-color)',
    },
  };

  return (
    <Layout>
      <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      {/* Progress Indicator */}
      <ProgressSteps currentStep="getting-started" />

      <div className="form-container">
        {isResumingByToken && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <span>Loading claim...</span>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="form-required-note">All fields are required unless marked as optional.</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="reporter_first_name">First Name</label>
              <input
                id="reporter_first_name"
                type="text"
                {...register('reporter_first_name', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'reporter_first_name', value: e.target.value }));
                  }
                })}
                className={errors.reporter_first_name ? 'error' : ''}
              />
              {errors.reporter_first_name && (
                <div className="error-message">{errors.reporter_first_name.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="reporter_last_name">Last Name</label>
              <input
                id="reporter_last_name"
                type="text"
                {...register('reporter_last_name', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'reporter_last_name', value: e.target.value }));
                  }
                })}
                className={errors.reporter_last_name ? 'error' : ''}
              />
              {errors.reporter_last_name && (
                <div className="error-message">{errors.reporter_last_name.message}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="reporter_phone">Phone Number</label>
              <Controller
                name="reporter_phone"
                control={control}
                render={({ field }) => (
                  <input
                    id="reporter_phone"
                    type="tel"
                    {...field}
                    placeholder="(555) 123-4567"
                    className={errors.reporter_phone ? 'error' : ''}
                    onKeyDown={(e) => {
                      if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      field.onChange(formatted);
                      dispatch(updateClaimField({ field: 'reporter_phone', value: formatted }));
                    }}
                  />
                )}
              />
              {errors.reporter_phone && (
                <div className="error-message">{errors.reporter_phone.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="owner_email">Email</label>
              <input
                id="owner_email"
                type="email"
                {...register('owner_email', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'owner_email', value: e.target.value }));
                  }
                })}
                className={errors.owner_email ? 'error' : ''}
              />
              {errors.owner_email && (
                <div className="error-message">{errors.owner_email.message}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="owner_address_1">Address Line 1</label>
              <input
                id="owner_address_1"
                type="text"
                autoComplete="section-owner address-line1"
                {...register('owner_address_1', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'owner_address_1', value: e.target.value }));
                  }
                })}
                className={errors.owner_address_1 ? 'error' : ''}
              />
              {errors.owner_address_1 && (
                <div className="error-message">{errors.owner_address_1.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="owner_address_2">Address Line 2 <span className="optional">(Optional)</span></label>
              <input
                id="owner_address_2"
                type="text"
                autoComplete="section-owner address-line2"
                {...register('owner_address_2', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'owner_address_2', value: e.target.value }));
                  }
                })}
                className={errors.owner_address_2 ? 'error' : ''}
              />
              {errors.owner_address_2 && (
                <div className="error-message">{errors.owner_address_2.message}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="owner_city">City</label>
              <input
                id="owner_city"
                type="text"
                autoComplete="section-owner address-level2"
                {...register('owner_city', {
                  onChange: (e) => {
                    dispatch(updateClaimField({ field: 'owner_city', value: e.target.value }));
                  }
                })}
                className={errors.owner_city ? 'error' : ''}
              />
              {errors.owner_city && (
                <div className="error-message">{errors.owner_city.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="owner_state">State</label>
              <Controller
                name="owner_state"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.owner_state} sx={muiSelectFormSx}>
                    <Select
                      {...field}
                      id="owner_state"
                      variant="outlined"
                      displayEmpty
                      inputProps={{ autoComplete: 'section-owner address-level1' }}
                      disabled={loadingStates}
                      value={
                        loadingStates || states.length === 0
                          ? ''
                          : states.some((s) => s.code === field.value)
                            ? field.value
                            : ''
                      }
                      onChange={(e) => {
                        const v = String(e.target.value);
                        field.onChange(v);
                        dispatch(updateClaimField({ field: 'owner_state', value: v }));
                      }}
                    >
                      <MenuItem value="">
                        <em>Select State</em>
                      </MenuItem>
                      {!loadingStates &&
                        states.map((state) => (
                          <MenuItem key={state.id} value={state.code}>
                            {state.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              />
              {errors.owner_state && (
                <div className="error-message">{errors.owner_state.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="owner_zip_code">Zip Code</label>
              <Controller
                name="owner_zip_code"
                control={control}
                render={({ field }) => (
                  <input
                    id="owner_zip_code"
                    type="text"
                    {...field}
                    placeholder="12345 or 12345-6789"
                    autoComplete="section-owner postal-code"
                    className={errors.owner_zip_code ? 'error' : ''}
                    onKeyDown={(e) => {
                      if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
                      if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                      if (!/^[\d-]$/.test(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => {
                      const sanitized = sanitizeZipInput(e.target.value);
                      field.onChange(sanitized);
                      dispatch(updateClaimField({ field: 'owner_zip_code', value: sanitized }));
                    }}
                  />
                )}
              />
              {errors.owner_zip_code && (
                <div className="error-message">{errors.owner_zip_code.message}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="facility_state">Facility State</label>
              <Controller
                name="facility_state"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.facility_state} sx={muiSelectFormSx}>
                    <Select
                      {...field}
                      id="facility_state"
                      variant="outlined"
                      displayEmpty
                      inputProps={{ autoComplete: 'section-facility' }}
                      disabled={loadingStates || loadingFacilityStates}
                      value={
                        loadingStates || loadingFacilityStates || availableFacilityStates.length === 0
                          ? ''
                          : availableFacilityStates.some((s) => s.code === field.value)
                            ? field.value
                            : ''
                      }
                      onChange={(e) => {
                        const v = String(e.target.value);
                        field.onChange(v);
                        dispatch(updateClaimField({ field: 'facility_state', value: v }));
                      }}
                    >
                      <MenuItem value="">
                        <em>Select State</em>
                      </MenuItem>
                      {!(loadingStates || loadingFacilityStates) &&
                        availableFacilityStates.map((state) => (
                          <MenuItem key={state.id} value={state.code}>
                            {state.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
              />
              {errors.facility_state && (
                <div className="error-message">{errors.facility_state.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="facility_city">Facility City</label>
              <Controller
                name="facility_city"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.facility_city} sx={muiSelectFormSx}>
                    <Select
                      {...field}
                      id="facility_city"
                      variant="outlined"
                      displayEmpty
                      inputProps={{ autoComplete: 'section-facility' }}
                      disabled={!facilityStateWatched || loadingCities || cities.length === 0}
                      value={
                        loadingCities || cities.length === 0
                          ? ''
                          : cities.includes(field.value || '')
                            ? field.value
                            : ''
                      }
                      onChange={(e) => {
                        const v = String(e.target.value);
                        field.onChange(v);
                        dispatch(updateClaimField({ field: 'facility_city', value: v }));
                        setValue('facility_id', '', { shouldValidate: false, shouldDirty: false });
                        dispatch(updateClaimField({ field: 'facility_id', value: '' }));
                        const stateCode =
                          getValues('facility_state') || claimData.facility_state;
                        void fetchFacilitiesList(stateCode, v);
                      }}
                    >
                      {loadingCities ? (
                        <MenuItem value="">
                          <em>Loading...</em>
                        </MenuItem>
                      ) : cityError ? (
                        <MenuItem value="">
                          {cityError}
                        </MenuItem>
                      ) : cities.length === 0 ? (
                        <MenuItem value="">
                          <em>No cities available</em>
                        </MenuItem>
                      ) : (
                        [
                          <MenuItem key="__city_placeholder" value="">
                            <em>Select City</em>
                          </MenuItem>,
                          ...cities.map((city: string) => (
                            <MenuItem key={city} value={city}>
                              {city}
                            </MenuItem>
                          )),
                        ]
                      )}
                    </Select>
                  </FormControl>
                )}
              />
              {errors.facility_city && (
                <div className="error-message">{errors.facility_city.message}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="facility_id">Facility</label>
              <Controller
                name="facility_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.facility_id} sx={muiSelectFormSx}>
                    <Select
                      {...field}
                      id="facility_id"
                      variant="outlined"
                      displayEmpty
                      inputProps={{ autoComplete: 'section-facility' }}
                      disabled={!facilityCityWatched || loadingFacilities}
                      value={
                        loadingFacilities || facilities.length === 0
                          ? ''
                          : facilities.some((f) => String(f.id) === String(field.value))
                            ? String(field.value)
                            : ''
                      }
                      onChange={(e) => {
                        const v = String(e.target.value);
                        field.onChange(v);
                        dispatch(updateClaimField({ field: 'facility_id', value: v }));
                      }}
                    >
                      {loadingFacilities ? (
                        <MenuItem value="">
                          <em>Loading...</em>
                        </MenuItem>
                      ) : facilityError ? (
                        <MenuItem value="">
                          {facilityError}
                        </MenuItem>
                      ) : facilities.length === 0 ? (
                        <MenuItem value="">
                          <em>No facilities found</em>
                        </MenuItem>
                      ) : (
                        [
                          <MenuItem key="__facility_placeholder" value="">
                            <em>Select Facility</em>
                          </MenuItem>,
                          ...facilities.map((facility) => (
                            <MenuItem key={facility.id} value={String(facility.id)}>
                              {facility.name}
                            </MenuItem>
                          )),
                        ]
                      )}
                    </Select>
                  </FormControl>
                )}
              />
              {errors.facility_id && (
                <div className="error-message">{errors.facility_id.message}</div>
              )}
            </div>
          </div>

          <div className="form-row">
          <div className="form-group">
            <label htmlFor="facility_unit">Unit Number</label>
            <input
              id="facility_unit"
              type="text"
              {...register('facility_unit')}
              className={errors.facility_unit ? 'error' : ''}
            />
            {errors.facility_unit && (
              <div className="error-message">{errors.facility_unit.message}</div>
            )}
          </div>
          <div className="form-group"></div>
          <div className="form-group">
            {config.APP_MODE !== 'production' && (
              <span>
                <button 
                  type="button"
                  onClick={() => {
                    loadTestData();
                  }} 
                  style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit' }}
                >
                  Load Test Data
                </button>
                <span style={{ color: '#666', fontSize: '0.9em' }}>
                  {' '}
                  (click 1: select FL state, click 2: select Bonita Springs city)
                </span>
              </span>
            )}
          </div>
          </div>

          <button type="submit" className="button button-primary button-full" disabled={isSubmitting}>
            {isSubmitting
              ? (claimData.claimId != null ? 'Saving...' : 'Creating...')
              : 'Continue to Incident Details'}
          </button>
        </form>
      </div>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: '#666', fontSize: '0.875rem' }}>
        v{packageJson.version}
      </div>
    </Layout>
  );
};

export default GetStarted;

